import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { authApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Wifi, Lock, User } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Preencha todos os campos");
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.login(username, password);
      login(response.data.token, response.data.user);
      toast.success(`Bem-vindo, ${response.data.user.name}!`);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const handleInit = async () => {
    setInitializing(true);
    try {
      const response = await authApi.init();
      if (response.data.username) {
        toast.success("Admin criado! Use: admin / admin123");
        setUsername("admin");
        setPassword("admin123");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Erro ao inicializar sistema");
    } finally {
      setInitializing(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Brand */}
      <div 
        className="hidden lg:flex lg:w-3/5 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1744868562210-fffb7fa882d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx0ZWxlY29tJTIwdGVjaG5pY2lhbiUyMHRvd2VyJTIwZmliZXIlMjBvcHRpYyUyMHNlcnZlciUyMHJhY2t8ZW58MHx8fHwxNzczMjM3NzE1fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <img 
              src="/tecplus-logo.png" 
              alt="Tecplus Telecom" 
              className="h-16 object-contain"
            />
          </div>
          
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Sistema de<br />
              <span className="text-[#FF4500]">Viabilidade</span><br />
              de Rede
            </h2>
            <p className="text-lg text-white/70 max-w-md">
              Consulte a viabilidade técnica de instalação em tempo real com base nas CTOs disponíveis.
            </p>
          </div>

          <div className="flex items-center gap-8 text-sm text-white/50">
            <span>Fiber OS v1.0</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>© 2026 Tecplus Telecom</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-8 bg-slate-950">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 justify-center mb-8">
            <img 
              src="/tecplus-logo.png" 
              alt="Tecplus Telecom" 
              className="h-12 object-contain"
            />
          </div>

          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-bold text-white">Acesse sua conta</h2>
            <p className="mt-2 text-slate-400">
              Entre com suas credenciais para continuar
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300">Usuário</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="username"
                  data-testid="username-input"
                  type="text"
                  placeholder="Digite seu usuário"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-12 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:ring-[#FF4500] focus:border-[#FF4500]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:ring-[#FF4500] focus:border-[#FF4500]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              className="w-full h-12 bg-[#FF4500] hover:bg-[#CC3700] text-white font-bold uppercase tracking-widest rounded-none"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="spinner" />
                  <span>Entrando...</span>
                </div>
              ) : (
                "Entrar"
              )}
            </Button>
          </form>

          <div className="pt-4 border-t border-slate-800">
            <p className="text-sm text-slate-500 text-center mb-4">
              Primeiro acesso? Inicialize o sistema.
            </p>
            <Button
              type="button"
              variant="outline"
              data-testid="init-button"
              disabled={initializing}
              onClick={handleInit}
              className="w-full h-10 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-none"
            >
              {initializing ? "Inicializando..." : "Inicializar Sistema"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
